import jsPDF from "jspdf";
import "jspdf-autotable";
import image from "../data/image";

/**
 * @param {object} studentResult - Student result data
 * @param {number} totalStudents - Total students count
 * @param {string} division - Division label
 * @param {{ returnBlob?: boolean }} options - If returnBlob is true, returns PDF Blob instead of saving (for print).
 * @returns {string | Blob | undefined} - File name when saving, or Blob when returnBlob is true.
 */
const generateStudentTermResultsReport = (studentResult, totalStudents, division, options = {}) => {
  if (!studentResult) return;

  const doc = new jsPDF("portrait", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ====== HEADER ======
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("ST. MARY'S JUNIOR SEMINARY - VISIGA", pageWidth / 2, 14, {
    align: "center",
  });

  doc.setFontSize(13);
  doc.setFont("helvetica", "normal");
  doc.text("SEMINARIAN TERM RESULTS REPORT", pageWidth / 2, 20, {
    align: "center",
  });

  // ====== STUDENT INFORMATION ======
  let y = 32;
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(0.5);
  doc.line(15, y - 4, pageWidth - 15, y - 4);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("RESULTS SUMMARY", 15, y);

  y += 8;
  doc.setFontSize(11);

  const info = [
    ["Student Name:", studentResult.studentName],
    ["Class:", studentResult.className],
    ["TermName:", studentResult.termName],
    ["Index Number:", studentResult.studentNumber],
    ["Combination:", studentResult.combination],
    ["Class Level:", studentResult.classType],
    ["Total Marks:", studentResult.totalMarks?.toString()],
    ["Average:", studentResult.average?.toString()],
    ["Division:", division || "N/A"],
    [
      "Position:",
      `${studentResult.positionText || "N/A"} out of ${totalStudents}`,
    ],
  ];

  info.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, 20, y);
    doc.setFont("helvetica", "normal");
    doc.text(value || "N/A", 60, y);
    y += 7;
  });

  // ====== SUBJECT RESULTS TABLE ======
  y += 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("SUBJECT RESULTS", 15, y);

  y += 5;
  const tableData = [];

  if (studentResult.subjects) {
    Object.entries(studentResult.subjects).forEach(([subject, details]) => {
      const hasData =
        details.exam10Average > 0 ||
        details.exam40Average > 0 ||
        details.exam50Marks > 0 ||
        details.totalMarks > 0;

      if (hasData) {
        tableData.push([
          subject,
          details.exam10Average?.toString() || "0",
          details.exam40Average?.toString() || "0",
          details.exam50Marks?.toString() || "0",
          details.totalMarks?.toString() || "0",
          details.grade || "N/A",
        ]);
      }
    });
  }

  doc.autoTable({
    startY: y,
    head: [
      ["Subject", "Exercise (10)", "Tests (40)", "Exam (50)", "Total (100)", "Grade"],
    ],
    body: tableData,
    theme: "grid",
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: "bold",
    },
    styles: {
      fontSize: 10,
      cellPadding: 2.5,
      halign: "center",
      valign: "middle",
    },
    alternateRowStyles: {
      fillColor: [248, 248, 248],
    },
    columnStyles: {
      0: { halign: "left", cellWidth: 55 },
      1: { cellWidth: 25 },
      2: { cellWidth: 25 },
      3: { cellWidth: 25 },
      4: { cellWidth: 25 },
      5: { cellWidth: 20 },
    },
  });


  const finalY = doc.lastAutoTable.finalY + 15;

  
  const stampWidth = 40;
  const stampHeight = 40;
  const stampX = pageWidth - stampWidth - 20; 

  
  if (image.stamp) {
    doc.addImage(image.stamp, "PNG", stampX, finalY, stampWidth, stampHeight);
  }

  
  const centerX = pageWidth / 2;
  let textY = finalY + 10;

  

  // ====== FOOTER ======
  doc.setFontSize(9);
  doc.setFont("helvetica", "italic");

  doc.text(
    "St. Mary's Junior Seminary Visiga (SIMS) System",
    pageWidth / 2,
    pageHeight - 15,
    { align: "center" }
  );

  doc.text(
    `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: "center" }
  );

  // ====== SAVE ======
  const fileName = `Student_Report_${
    studentResult.studentName?.replace(/\s+/g, "_") || "Unknown"
  }_${new Date().toISOString().split("T")[0]}.pdf`;

  if (options.returnBlob) {
    return doc.output("blob");
  }
  doc.save(fileName);
  return fileName;
};

export default generateStudentTermResultsReport;
